import React, { useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";

function Spinner() {
  return (
    <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-amber-400/60 border-t-transparent" />
  );
}

function MarkdownPreview({ md }) {
  // Lightweight rendering: preserve paragraph breaks, bold **x**, headings, lists.
  // Keep it small — full markdown libs are overkill for the preview.
  const lines = (md || "").split("\n");
  return (
    <div className="prose prose-invert max-w-none text-sm leading-relaxed">
      {lines.map((ln, i) => {
        if (ln.startsWith("### ")) return <h3 key={i} className="mt-4 text-amber-300">{ln.slice(4)}</h3>;
        if (ln.startsWith("## "))  return <h2 key={i} className="mt-5 text-amber-200">{ln.slice(3)}</h2>;
        if (ln.startsWith("# "))   return <h1 key={i} className="mt-6 text-amber-100">{ln.slice(2)}</h1>;
        if (ln.startsWith("- "))   return <div key={i} className="ml-4 text-slate-300">• {ln.slice(2)}</div>;
        if (ln.startsWith("> "))   return <blockquote key={i} className="border-l-2 border-amber-400/40 pl-3 italic text-slate-400">{ln.slice(2)}</blockquote>;
        if (ln.trim() === "")      return <div key={i} className="h-2" />;
        const withBold = ln.split(/(\*\*[^*]+\*\*)/g).map((seg, j) =>
          seg.startsWith("**") && seg.endsWith("**")
            ? <strong key={j} className="text-slate-100">{seg.slice(2, -2)}</strong>
            : <span key={j}>{seg}</span>
        );
        return <p key={i} className="text-slate-300">{withBold}</p>;
      })}
    </div>
  );
}

export default function ConsultPage() {
  const [question, setQuestion]   = useState("");
  const [ring, setRing]           = useState("six");
  const [includeWeb, setIncludeWeb] = useState(true);
  const [busy, setBusy]           = useState(false);
  const [err, setErr]             = useState("");
  const [result, setResult]       = useState(null);
  const [licenseCode, setLicenseCode] = useState("");

  async function submit(e) {
    e.preventDefault();
    setErr("");
    setResult(null);
    if (question.trim().length < 10) {
      setErr("Please describe your scenario in at least a couple sentences (10+ characters).");
      return;
    }
    setBusy(true);
    try {
      const body = {
        question:       question.trim(),
        ring,
        include_search: includeWeb,
        include_library: true,
        library_top_k:  6,
        preview:        !licenseCode.trim(),
      };
      if (licenseCode.trim()) body.license_code = licenseCode.trim();
      const r = await axios.post(`${API}/consultation/generate`, body, { timeout: 120000 });
      setResult(r.data);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2.message || "Consultation failed.");
    } finally {
      setBusy(false);
    }
  }

  const samplePrompts = [
    "I own a barber shop and a small clothing store. How can I use AI to run both more efficiently without firing my staff?",
    "I run a 3-truck plumbing business. What's the highest-ROI AI investment I can make in the next 90 days?",
    "I'm a solo therapist with a private practice. How do I use AI for intake and scheduling without crossing ethical lines?",
    "We're a 12-person law firm. Where should AI live in our workflow — and where must it not?",
  ];

  return (
    <div className="mx-auto max-w-4xl px-4 py-8" data-testid="consult-page">
      <header className="mb-8">
        <div className="mb-2 inline-block rounded-full border border-amber-400/40 bg-amber-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-amber-300">
          Intelligent Database · Cross-Domain
        </div>
        <h1 className="text-3xl font-semibold text-slate-100 sm:text-4xl">
          One consultation. Every domain you run.
        </h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-400">
          Tell us about <em className="text-slate-300">all</em> your businesses in plain English.
          We braid them through the Federation&apos;s Ring of Six perspectives, ground every claim in our
          sealed library + live web sources, and hand you back a downloadable consultation document
          — citations included, FACT/INFERENCE/ASSUMPTION labels included, hallucinations gated out.
          One token. One report. Multiple businesses. Yours to keep.
        </p>
      </header>

      <form onSubmit={submit} className="mb-6 rounded-2xl border border-slate-700/60 bg-slate-900/50 p-5 backdrop-blur-sm" data-testid="consult-form">
        <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-400">
          Your situation
        </label>
        <textarea
          data-testid="consult-question"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          rows={6}
          placeholder="Describe what you own, what you're trying to figure out, what's already on your plate. The more specific, the sharper the consultation."
          className="mb-3 w-full rounded-lg border border-slate-700 bg-slate-950/60 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:border-amber-400/60 focus:outline-none"
        />

        <div className="mb-3 flex flex-wrap gap-1.5 text-[11px]">
          <span className="text-slate-500">try:</span>
          {samplePrompts.map((s, i) => (
            <button
              key={i}
              type="button"
              data-testid={`sample-${i}`}
              onClick={() => setQuestion(s)}
              className="rounded border border-slate-700 px-2 py-0.5 text-slate-400 hover:bg-slate-800/80 hover:text-slate-200"
            >
              {s.slice(0, 42)}…
            </button>
          ))}
        </div>

        <div className="mb-4 grid gap-3 sm:grid-cols-2">
          <label className="text-xs text-slate-400">
            <div className="mb-1 font-semibold uppercase tracking-wider">Ring depth</div>
            <select
              data-testid="consult-ring"
              value={ring}
              onChange={(e) => setRing(e.target.value)}
              className="w-full rounded border border-slate-700 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 focus:border-amber-400/60 focus:outline-none"
            >
              <option value="default">Default · 3 lenses (fast)</option>
              <option value="six">Ring of Six · canonical (recommended)</option>
              <option value="12">Ring of Twelve · deep adversarial</option>
            </select>
          </label>
          <label className="flex items-end gap-2 text-xs text-slate-400">
            <input
              data-testid="consult-web"
              type="checkbox"
              checked={includeWeb}
              onChange={(e) => setIncludeWeb(e.target.checked)}
              className="h-4 w-4 rounded border-slate-600 bg-slate-950"
            />
            <span>Include live web grounding (Brave Search)</span>
          </label>
        </div>

        <div className="mb-4">
          <label className="text-xs text-slate-400">
            <div className="mb-1 font-semibold uppercase tracking-wider">License / token code (optional)</div>
            <input
              data-testid="consult-license"
              value={licenseCode}
              onChange={(e) => setLicenseCode(e.target.value)}
              placeholder="M-D-KEYWORD (paste your code for the full report; otherwise you get a free preview)"
              className="w-full rounded border border-slate-700 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-amber-400/60 focus:outline-none"
            />
          </label>
        </div>

        {err && <div className="mb-3 rounded border border-rose-500/40 bg-rose-500/10 p-2 text-xs text-rose-300" data-testid="consult-error">{err}</div>}

        <div className="flex flex-wrap items-center gap-3">
          <button
            data-testid="consult-submit"
            type="submit"
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-lg bg-amber-500 px-5 py-2 text-sm font-semibold text-slate-950 transition hover:bg-amber-400 disabled:opacity-50"
          >
            {busy && <Spinner />}
            {busy ? "Braid synthesizing…" : (licenseCode.trim() ? "Generate full consultation" : "Get free preview")}
          </button>
          <a
            href="https://keywebster.gumroad.com/l/ophtywm"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-slate-400 underline-offset-2 hover:text-amber-300 hover:underline"
            data-testid="consult-buy-token-link"
          >
            Don&apos;t have a token? Get one for $5 →
          </a>
        </div>
        <p className="mt-3 text-[11px] text-slate-500">
          Powered by the Federation Core · Ring of Perspectives · Brave Search grounding · 95% Truth Gate.
          Anonymous = preview synthesis only. Token unlocks full per-lens findings + downloadable markdown.
        </p>
      </form>

      {result && (
        <section className="rounded-2xl border border-amber-400/40 bg-amber-500/[0.03] p-6" data-testid="consult-result">
          <header className="mb-4 flex flex-wrap items-center gap-3">
            <span className="rounded-full bg-amber-500/15 px-3 py-1 text-[11px] font-semibold text-amber-300">
              {result.preview ? "Preview" : "Full consultation"}
            </span>
            <span className="text-xs text-slate-400">tier · {result.tier}</span>
            <span className="text-xs text-slate-400">ring · {result.ring}</span>
            <span className="text-xs text-slate-400">library hits · {result.n_library_hits}</span>
            <span className="text-xs text-slate-400">web hits · {result.n_web_hits}</span>
            {result.synthesis?.confidence !== undefined && (
              <span className="text-xs font-semibold text-emerald-300">
                confidence · {Math.round((result.synthesis.confidence || 0) * 100)}%
              </span>
            )}
            <a
              href={`${API}/consultation/${result.consultation_id}/download`}
              className="ml-auto rounded border border-amber-400/60 px-3 py-1 text-xs font-semibold text-amber-300 hover:bg-amber-500/10"
              data-testid="consult-download"
            >
              ⬇ Download .md
            </a>
          </header>

          <div className="rounded-xl border border-slate-700/60 bg-slate-950/40 p-5" data-testid="consult-markdown">
            <MarkdownPreview md={result.markdown || ""} />
          </div>

          {result.preview && (
            <div className="mt-4 rounded-lg border border-amber-400/40 bg-amber-500/[0.05] p-4 text-sm text-slate-300" data-testid="consult-upsell">
              <b className="text-amber-200">This is the free preview.</b> The full consultation unlocks
              every lens&apos;s individual findings, the complete citation trail, and the downloadable
              markdown. Redeem a Federation bearer token (or paste a license code above) to receive it.
              <div className="mt-2">
                <a
                  href="https://keywebster.gumroad.com/l/ophtywm"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded bg-amber-500 px-3 py-1 text-xs font-semibold text-slate-950 hover:bg-amber-400"
                >
                  Get a token · $5
                </a>
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
